import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useHtmlByUrl } from "../../../common/html_artifact_store.es6";

/**
 * The approval card's preview of a generated document.
 *
 * The two kinds need opposite things from an iframe, which is why this is one
 * component with a branch rather than two call sites:
 *
 *   • A 3D object is interactive and timeless. It is shown as-is, with pointer
 *     events ON — turning it IS the thing the author is approving. (The box it
 *     becomes uses the Webpage plugin, which disables pointer events in the
 *     editor so the box stays draggable, and leaves them on in the visor. So
 *     this card is the only place in the editor where the object can actually
 *     be handled.)
 *
 *   • A motion document never starts a clock of its own: it draws t=0 and then
 *     waits for `motion:seek` messages, because in a real scene the playhead —
 *     or, during export, the recorder's seek bridge — owns the time. Shown
 *     plainly it would look like a still image and every author would reject a
 *     working scene. So the card drives it: one rAF loop, looping over the
 *     duration the generator returned.
 */
const SceneVisualPreview = ({ kind, htmlUrl, durationSec, height }) => {
  const { html, directSrc } = useHtmlByUrl(htmlUrl);
  const frameRef = useRef(null);

  useEffect(() => {
    if (kind !== "motion" || !htmlUrl) return undefined;

    const duration = Math.max(Number(durationSec) || 0, 0.5);
    let raf = null;
    let startedAt = null;
    let stopped = false;

    const tick = (now) => {
      if (stopped) return;
      if (startedAt === null) startedAt = now;
      const t = ((now - startedAt) / 1000) % duration;
      const win = frameRef.current?.contentWindow;
      // Same-origin is not guaranteed (the document is served from blob
      // storage), so the message is posted with "*" exactly as the plugin
      // does; a frame that has not loaded yet simply misses a few ticks.
      try {
        win?.postMessage({ type: "motion:seek", t, subframes: 1 }, "*");
      } catch {
        /* the frame is gone or not ready — the next tick will find it */
      }
      raf = window.requestAnimationFrame(tick);
    };

    raf = window.requestAnimationFrame(tick);
    return () => {
      stopped = true;
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [kind, htmlUrl, durationSec]);

  if (!htmlUrl) return null;

  return (
    <iframe
      ref={frameRef}
      srcDoc={directSrc ? undefined : html || ""}
      src={directSrc || undefined}
      title={kind === "motion" ? "motion-preview" : "3d-preview"}
      style={{
        width: "100%",
        height: height || 360,
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        background: "#0b1020",
        display: "block",
      }}
      // The 3D viewer is handled by the author here; the motion document is
      // driven from outside and has nothing to click.
      scrolling="no"
      sandbox="allow-scripts"
    />
  );
};

SceneVisualPreview.propTypes = {
  kind: PropTypes.oneOf(["3d", "motion"]).isRequired,
  htmlUrl: PropTypes.string,
  durationSec: PropTypes.number,
  height: PropTypes.number,
};

export default SceneVisualPreview;
